import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { BreakPriceDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomState } from "@SupplyLand/Store/Data/SupplyLand/SupplyLandReducer";
import ProductListMobileCard from "@SupplyLand/Widgets/ProductList/ProductListMobileCard";
import VariantTable from "@SupplyLand/Widgets/ProductList/VariantTable";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & HasProduct & HasParentProductId;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    filterContainer?: GridContainerProps;
    wrapper?: InjectableCss;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    searchButton?: InjectableCss;
    searchLabel?: InjectableCss;
    searchLabelContainer?: InjectableCss;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
    },
    filterContainer: {
        css: css`
            position: sticky;
            align-self: flex-start;
            top: 10px;
            flex-basis: 250px;
            margin-right: 20px;
            padding: 10px;
            button {
                width: 100%;
                border-radius: 10px;
                color: black;
            }
            label {
                text-align: center;
            }
            p {
                width: 100%;
                font-weight: bold;
                text-align: center;
                margin-bottom: 0;
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            input[type="number"] {
                -moz-appearance: textfield;
            }
        `,
        gap: 10,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            .searchForm {
                display: contents;
            }
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    titleText: {
        variant: "h1",
        css: css`
            font-size: 32px;
        `,
    },
    searchButton: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    searchLabel: {
        css: css`
            width: 20%;
            text-align: center;
        `,
    },
    searchLabelContainer: {
        css: css`
            width: 100%;
            display: flex;
            padding: 0 15px 0 15px;
        `,
    },
};

export interface VariantTableStyles {
    variantTable?: DataTablePresentationProps;
    tableContainer?: InjectableCss;
    priceCell?: InjectableCss;
    header?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToCartButton?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    variantTableRow?: DataTableRowProps;
    basicVariantTableRow?: DataTableRowProps;
    leftTableContent?: InjectableCss;
    rightTableContent?: InjectableCss;
    addToCartColumn?: InjectableCss;
}

export const variantTableStyles: VariantTableStyles = {
    tableContainer: {
        css: css`
            margin-bottom: 10px;
            width: 100%;
            text-align: center;
        `,
    },

    variantTable: {
        css: css`
            width: 100%;
            td {
                border: 0.5px solid black;
                text-align: center;
                padding: 0px 8px;
            }
            th {
                border: 0.5px solid black;
                text-align: center;
                max-width: 130px;
                white-space: normal;
                background: rgb(251 241 200);
                padding: 0px 8px;
            }
        `,
    },
    variantTableRow: {
        css: css`
            background: white;
            a {
                text-decoration: underline;
            }
        `,
        evenRowCss: css`
            background: rgb(251 241 200);
        `,
    },
    basicVariantTableRow: {
        css: css`
            background: white;
            a {
                text-decoration: underline;
            }
        `,
        evenRowCss: css`
            background: white;
        `,
    },
    priceCell: {
        css: css`
            border-left: none !important;
            border-right: none !important;
            background: none;
        `,
    },
    header: {
        css: css`
            border-bottom: 0px solid !important;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            color: ${getColor("secondary")};
            box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            margin-left: 20px;
            margin-right: 20px;
            border-radius: 8px;
        `,
    },
    productAddToCartButton: {
        css: css`
            color: ${getColor("secondary")};
            box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            margin-left: 20px;
            margin-right: 20px;
            border-radius: 8px;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
                padding-left: 15px;
                text-align-last: center;
            `,
        },
    },
    leftTableContent: {
        css: css`
            font-weight: bold;
            float: left;
        `,
    },
    rightTableContent: {
        css: css`
            float: right;
        `,
    },
    addToCartColumn: {
        css: css`
            min-width: 250px;
        `,
    },
};

const mapStateToProps = (state: CustomState) => {
    return {
        productData: state.data.products,
        productInfoData: state.pages.productDetails,
        isLoading: state.data.products.isLoading,
        pricing: state.supplyLandData.variantPrices,
    };
};

const styles = pageTitleStyles;

const ProductDetailsVariantTable: React.FC<Props> = ({
    product,
    parentProductId,
    productData,
    productInfoData,
    pricing,
}) => {
    let parentProduct = productData.byId[product.id];

    if (getIsWebCrawler() && parentProduct.productNumber === "KraftBoxes") {
        return null;
    }

    const paths = Object.keys(productData.idByPath);

    if (!product.isVariantParent) {
        parentProduct = productData.byId[parentProductId];
    }
    if (!parentProduct || parentProduct.variantTraits == null) {
        return null;
    }

    const isParentChildConnector =
        parentProduct.variantTraits?.some(variant => variant.nameDisplay === "Parent Child Connector") ||
        parentProduct.productNumber === "KraftBoxes";

    let variantChildren = [] as ProductModel[];
    paths.forEach(path => {
        if (path.substring(0, path.indexOf("?")) === parentProduct.canonicalUrl) {
            const child = productData.byId[productData.idByPath[path]];
            variantChildren.push(child);
        }
    });

    if (
        !product.isVariantParent &&
        (!isParentChildConnector || (parentProduct.variantTraits && parentProduct.variantTraits.length < 2))
    ) {
        const variantChild = variantChildren.find(x => x.id === product.id);
        variantChildren = [];
        if (variantChild) {
            variantChildren.push(variantChild);
        }
    }

    // Price Headers
    let breakPriceHeaders = [] as BreakPriceDto[];
    const amountOfBreaks = 0;

    variantChildren.forEach(child => {
        const childPrice = pricing[child.id];
        if (childPrice && childPrice.actualBreakPrices && childPrice.actualBreakPrices.length > amountOfBreaks) {
            breakPriceHeaders = childPrice.actualBreakPrices;
        }
    });

    let priceHeaders = [] as string[];
    breakPriceHeaders.forEach(price => {
        priceHeaders.push(price.breakQty.toString());
    });

    if (priceHeaders.length > 5) {
        priceHeaders = priceHeaders.slice(0, 5);
    }

    const variant = variantChildren[0];

    let differentMinimumOrderQuantities = false;
    variantChildren.every(child => {
        if (child.minimumOrderQty !== variant.minimumOrderQty) {
            differentMinimumOrderQuantities = true;
            return false;
        }
        return true;
    });

    if (variant && variant.minimumOrderQty && !differentMinimumOrderQuantities) {
        priceHeaders[0] = variant.minimumOrderQty.toString();
    }
    if (
        parentProduct.productNumber === "KraftBoxes" ||
        parentProduct.productNumber === "MultiDepthBoxes" ||
        parentProduct.productNumber === "TallBoxes"
    ) {
        priceHeaders[0] = "25";
    }
    // End Price Headers

    // Standard product, display in single row
    if (!isParentChildConnector && product.isVariantParent) {
        const singleRowProduct = { ...variantChildren[0] };
        singleRowProduct.productNumber = parentProduct.productNumber;

        variantChildren = [];
        variantChildren.push(singleRowProduct);
    }

    // Grandparent products
    if (isParentChildConnector && parentProduct.variantTraits && parentProduct.variantTraits.length > 1) {
        const variantGroupings: ProductModel[] = [];
        variantChildren.forEach(child => {
            const connectorId = parentProduct?.variantTraits?.find(
                variant => variant.nameDisplay === "Parent Child Connector",
            )?.id;
            const connectorNumber = child?.childTraitValues?.find(
                variant => variant.styleTraitId === connectorId,
            )?.value;

            const nonConnectorVariants = parentProduct?.variantTraits?.filter(
                variant => variant.nameDisplay !== "Parent Child Connector",
            );
            let variantId = "";
            if (nonConnectorVariants) {
                variantId = nonConnectorVariants[0].id;
            }

            const childSelection = productInfoData.variantSelection[variantId + connectorNumber];

            if (variantGroupings.find(variant => variant.productNumber === connectorNumber) == null) {
                const groupingProduct = {
                    id: child.id,
                    unitOfMeasures: parentProduct.unitOfMeasures,
                    productNumber: connectorNumber,
                    attributeTypes: child.attributeTypes,
                    childTraitValues: child.childTraitValues,
                    minimumOrderQty: child.minimumOrderQty,
                    properties: {},
                } as ProductModel;
                groupingProduct.properties[child.id] = child.id;
                variantGroupings.push(groupingProduct);
            } else {
                const grouping = variantGroupings.find(variant => variant.productNumber === connectorNumber);
                if (grouping) {
                    grouping.properties[child.id] = child.id;
                    if (child.childTraitValues?.some(trait => trait.id === childSelection)) {
                        grouping.id = child.id;
                    }
                    if (grouping.childTraitValues && child.childTraitValues) {
                        grouping.childTraitValues = grouping.childTraitValues.concat(child.childTraitValues);
                    }
                }
            }
        });
        variantChildren = variantGroupings;
    }
    // End Grandparents

    const [filteredVariantChildren, setVariantChildren] = React.useState(variantChildren);

    const parseFraction = (value: string) => {
        const n = value.split(" ");
        const whole = parseFloat(n[0]);
        let decimal = 0;
        if (n.length > 1) {
            if (n[1] === "1/8") {
                decimal = 0.125;
            }
            if (n[1] === "1/4") {
                decimal = 0.25;
            }
            if (n[1] === "3/8") {
                decimal = 0.375;
            }
            if (n[1] === "1/2") {
                decimal = 0.5;
            }
            if (n[1] === "5/8") {
                decimal = 0.625;
            }
            if (n[1] === "3/4") {
                decimal = 0.75;
            }
            if (n[1] === "7/8") {
                decimal = 0.875;
            }
        }
        return whole + decimal;
    };

    const variantTableFilter = (length: number, width: number, height: number) => {
        let filteredChildren = [] as ProductModel[];
        variantChildren.every(product => {
            const atts = product.attributeTypes?.find(a => a.label === "Inside Dims. (L x W x H)")?.attributeValues;
            let dimensions = "";
            if (atts) {
                dimensions = atts[0].value;
            }
            const [prodLength, prodWidth, prodHeight] = dimensions.split(" x ");
            const prodLengthDecimal = parseFraction(prodLength);
            const prodWidthDecimal = parseFraction(prodWidth);
            const prodHeightDecimal = parseFraction(prodHeight);

            if (length === prodLengthDecimal && width === prodWidthDecimal && height === prodHeightDecimal) {
                filteredChildren = [];
                filteredChildren.push(product);
                return false;
            }

            if (
                (length >= prodLengthDecimal - 1 && length <= prodLengthDecimal + 1 && width === 0 && height === 0) ||
                (length >= prodLengthDecimal - 1 &&
                    length <= prodLengthDecimal + 1 &&
                    width >= prodWidthDecimal - 1 &&
                    width <= prodWidthDecimal + 1 &&
                    height === 0) ||
                (length >= prodLengthDecimal - 1 &&
                    length <= prodLengthDecimal + 1 &&
                    width >= prodWidthDecimal - 1 &&
                    width <= prodWidthDecimal + 1 &&
                    height >= prodHeightDecimal - 1 &&
                    height <= prodHeightDecimal + 1) ||
                (length === 0 &&
                    width >= prodWidthDecimal - 1 &&
                    width <= prodWidthDecimal + 1 &&
                    height >= prodHeightDecimal - 1 &&
                    height <= prodHeightDecimal + 1) ||
                (length === 0 && width >= prodWidthDecimal - 1 && width <= prodWidthDecimal + 1 && height === 0) ||
                (length === 0 && width === 0 && height >= prodHeightDecimal - 1 && height <= prodHeightDecimal + 1)
            ) {
                filteredChildren.push(product);
            }
            return true;
        });
        if (filteredChildren.length === 0 && (length > 36 || width > 36 || height > 36)) {
            filteredChildren.push(variantChildren[variantChildren.length - 1]);
        }
        if (filteredChildren.length === 0 && (length < 4 || width < 4 || height < 4)) {
            filteredChildren.push(variantChildren[0]);
        }
        if (filteredChildren.length === 0) {
            variantChildren.forEach(x => filteredChildren.push(x));
        }
        return filteredChildren;
    };

    const variantTableButtonFilter = (minLength: number, maxLength: number) => {
        let filteredChildren = [] as ProductModel[];
        variantChildren.forEach(product => {
            const atts = product.attributeTypes?.find(a => a.label === "Inside Dims. (L x W x H)")?.attributeValues;
            let dimensions = "";
            if (atts) {
                dimensions = atts[0].value;
            }
            const prodLength = dimensions.split(" x ")[0];
            const prodLengthDecimal = parseFraction(prodLength);
            if (prodLengthDecimal >= minLength - 0.99 && prodLengthDecimal <= maxLength + 0.99) {
                filteredChildren.push(product);
            }
        });
        if (filteredChildren.length === 0) {
            filteredChildren = variantChildren;
        }
        setVariantChildren(filteredChildren);
    };

    const searchFormClear = () => {
        document.getElementById("searchLength")?.innerHTML === "";
        document.getElementById("searchWidth")?.innerHTML === "";
        document.getElementById("searchHeight")?.innerHTML === "";

        setVariantChildren(variantChildren);
    };

    const searchFormSubmit = (e: any) => {
        e.preventDefault();
        const length = parseFloat(
            (document.getElementById("searchLength") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchLength") as HTMLInputElement).value,
        );
        const width = parseFloat(
            (document.getElementById("searchWidth") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchWidth") as HTMLInputElement).value,
        );
        const height = parseFloat(
            (document.getElementById("searchHeight") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchHeight") as HTMLInputElement).value,
        );

        setVariantChildren(variantTableFilter(length, width, height));
    };

    if (
        !product.isVariantParent &&
        (!isParentChildConnector || (parentProduct.variantTraits && parentProduct.variantTraits.length < 2))
    ) {
        if (filteredVariantChildren.length > 1) {
            setVariantChildren(variantChildren);
        }
    }

    const leftTableContent = productData.byId[product.id].properties["table_Header_Left"];
    const rightTableContent = productData.byId[product.id].properties["table_Header_Right"];
    const leftTableFooterContent = productData.byId[product.id].properties["table_Footer_Left"];
    const rightTableFooterContent = productData.byId[product.id].properties["table_Footer_Right"];
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Hidden above="md" {...styles.wrapper}>
                    <ProductListMobileCard
                        variantChildren={variantChildren}
                        product={product}
                        parentProductId={parentProductId}
                        priceHeaders={priceHeaders}
                    ></ProductListMobileCard>
                </Hidden>
                <Hidden below="lg" {...styles.wrapper}>
                    {product.productNumber === "KraftBoxes" && (
                        <GridContainer {...styles.filterContainer}>
                            <Typography variant="p">Search by Size</Typography>
                            <StyledWrapper {...styles.searchLabelContainer}>
                                <StyledWrapper {...styles.searchLabel}>
                                    <Typography weight={700}>L</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchLabel}>
                                    <Typography weight={700}>x</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchLabel}>
                                    <Typography weight={700}>W</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchLabel}>
                                    <Typography weight={700}>x</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchLabel}>
                                    <Typography weight={700}>H</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <form className="searchForm">
                                <GridItem width={4}>
                                    <TextField type="number" max={100} id="searchLength" />
                                </GridItem>
                                <GridItem width={4}>
                                    <TextField type="number" max={100} id="searchWidth" />
                                </GridItem>
                                <GridItem width={4}>
                                    <TextField type="number" max={100} id="searchHeight" />
                                </GridItem>
                                <Button
                                    variant="primary"
                                    {...styles.searchButton}
                                    type="submit"
                                    onClick={e => searchFormSubmit(e)}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="primary"
                                    {...styles.searchButton}
                                    type="reset"
                                    onClick={() => searchFormClear()}
                                >
                                    Clear
                                </Button>
                            </form>
                            <Typography variant="p">Filter by Size Range</Typography>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(3, 8)}>
                                    3" - 8"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(9, 11)}>
                                    9" - 11"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(12, 13)}>
                                    12" - 13"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(14, 15)}>
                                    14" - 15"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(16, 17)}>
                                    16" - 17"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(18, 19)}>
                                    18" - 19"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(20, 23)}>
                                    20" - 23"
                                </Button>
                            </GridItem>
                            <GridItem width={12}>
                                <Button variant="primary" onClick={e => variantTableButtonFilter(24, 48)}>
                                    24" - 48"
                                </Button>
                            </GridItem>
                        </GridContainer>
                    )}
                    <StyledWrapper {...variantTableStyles.tableContainer}>
                        <Typography {...variantTableStyles.leftTableContent}>{leftTableContent}</Typography>
                        <Typography {...variantTableStyles.rightTableContent}>{rightTableContent}</Typography>
                        <VariantTable
                            product={product}
                            parentProduct={parentProduct}
                            variantChildren={filteredVariantChildren}
                            priceHeaders={priceHeaders}
                            isParentChildConnector={isParentChildConnector}
                        />
                        <Typography {...variantTableStyles.leftTableContent}>{leftTableFooterContent}</Typography>
                        <Typography {...variantTableStyles.rightTableContent}>{rightTableFooterContent}</Typography>
                    </StyledWrapper>
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(withProduct(connect(mapStateToProps)(ProductDetailsVariantTable))),
    definition: {
        displayName: "Variant Table",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
